import React, { Component, ReactNode, ErrorInfo } from 'react';
import './ErrorBoundary.css';

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  retryCount: number;
  copied: boolean;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo, errorId: string) => void;
  enableRetry?: boolean;
  maxRetries?: number;
  showErrorDetails?: boolean;
  errorTitle?: string;
  errorMessage?: string;
  className?: string;
  level?: 'page' | 'section' | 'component';
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = this.state.errorId || `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Log error details
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Error ID:', errorId);

    // Update state with error info
    this.setState({
      errorInfo,
      errorId,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo, errorId);
    }

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo, errorId);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetOnPropsChange && resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (resetKey, idx) => prevProps.resetKeys?.[idx] !== resetKey
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo, errorId: string) => {
    // In a real application, you would send this to an error reporting service
    // like Sentry, LogRocket, Bugsnag, etc.
    try {
      const errorData = {
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: 'anonymous', // Replace with actual user ID if available
      };

      // Example: Send to error reporting service
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData),
      // });

      console.log('Error logged to service:', errorData);
    } catch (loggingError) {
      console.error('Failed to log error to service:', loggingError);
    }
  };

  private resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
    });
  };

  private handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: null,
        retryCount: prevState.retryCount + 1,
      }));
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private copyErrorDetails = () => {
    const { error, errorInfo, errorId } = this.state;
    const errorDetails = `
Error ID: ${errorId}
Error: ${error?.message}
Stack: ${error?.stack}
Component Stack: ${errorInfo?.componentStack}
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
    `.trim();

    navigator.clipboard.writeText(errorDetails).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }).catch(() => {
      console.error('Failed to copy error details');
    });
  };

  private getErrorLevel = () => {
    const { level = 'component' } = this.props;
    return level;
  };

  private renderErrorIcon = () => {
    const level = this.getErrorLevel();
    
    if (level === 'page') {
      return (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    }

    return (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  };

  private renderFallbackUI = () => {
    const {
      enableRetry = true,
      maxRetries = 3,
      showErrorDetails = process.env.NODE_ENV === 'development',
      errorTitle,
      errorMessage,
    } = this.props;

    const { error, errorInfo, errorId, retryCount } = this.state;
    const level = this.getErrorLevel();
    const canRetry = enableRetry && retryCount < maxRetries;

    const defaultTitle = level === 'page' 
      ? 'Something went wrong with this page'
      : level === 'section'
      ? 'This section failed to load'
      : 'Something went wrong';

    const defaultMessage = level === 'page'
      ? 'We encountered an unexpected error while loading this page. Please try refreshing or contact support if the problem persists.'
      : 'We encountered an unexpected error. Please try again or refresh the page.';

    return (
      <div className={`error-boundary error-boundary--${level}`} role="alert" aria-live="assertive">
        <div className="error-boundary__content">
          <div className="error-boundary__icon">
            {this.renderErrorIcon()}
          </div>

          <div className="error-boundary__message">
            <h2 className="error-boundary__title">
              {errorTitle || defaultTitle}
            </h2>
            <p className="error-boundary__description">
              {errorMessage || defaultMessage}
            </p>

            {errorId && (
              <p className="error-boundary__error-id">
                Error ID: <code>{errorId}</code>
              </p>
            )}

            {retryCount > 0 && (
              <p className="error-boundary__retry-info">
                Retry attempt: {retryCount} of {maxRetries}
              </p>
            )}
          </div>

          <div className="error-boundary__actions">
            {canRetry && (
              <button
                onClick={this.handleRetry}
                className="error-boundary__button error-boundary__button--primary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                </svg>
                Try Again
              </button>
            )}

            {level === 'page' && (
              <>
                <button
                  onClick={this.handleReload}
                  className="error-boundary__button error-boundary__button--secondary"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 2v6h6" />
                    <path d="M21 12A9 9 0 0 0 6 5.3L3 8" />
                    <path d="M21 22v-6h-6" />
                    <path d="M3 12a9 9 0 0 0 15 6.7l3-2.7" />
                  </svg>
                  Reload Page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="error-boundary__button error-boundary__button--secondary"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9,22 9,12 15,12 15,22" />
                  </svg>
                  Go Home
                </button>
              </>
            )}
          </div>

          {showErrorDetails && error && (
            <details className="error-boundary__details">
              <summary className="error-boundary__details-summary">
                Technical Details
              </summary>
              <div className="error-boundary__details-content">
                <div className="error-boundary__error-section">
                  <h4>Error Message:</h4>
                  <pre className="error-boundary__error-text">{error.message}</pre>
                </div>

                {error.stack && (
                  <div className="error-boundary__error-section">
                    <h4>Stack Trace:</h4>
                    <pre className="error-boundary__error-text">{error.stack}</pre>
                  </div>
                )}

                {errorInfo?.componentStack && (
                  <div className="error-boundary__error-section">
                    <h4>Component Stack:</h4>
                    <pre className="error-boundary__error-text">{errorInfo.componentStack}</pre>
                  </div>
                )}

                <button
                  onClick={this.copyErrorDetails}
                  className="error-boundary__copy-button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy Error Details
                </button>
              </div>
            </details>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { hasError } = this.state;
    const { children, fallback, className = '' } = this.props;

    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return <div className={`error-boundary-wrapper ${className}`}>{fallback}</div>;
      }

      // Default fallback UI
      return <div className={`error-boundary-wrapper ${className}`}>{this.renderFallbackUI()}</div>;
    }

    // No error, render children normally
    return children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Predefined error boundaries for common use cases
export const PageErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="page" />
);

export const SectionErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="section" />
);

export const ComponentErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="component" />
);

export default ErrorBoundary;
